
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { PropBot } from '@/types/prop-bots';
import { formatDate } from '@/lib/utils';
import { Calendar, Info, Bot, Shield, Award, TrendingUp, GitBranch, ExternalLink } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useTour } from '@/contexts/TourContext';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/hooks/use-mobile';
import { useNavigate } from 'react-router-dom';

interface EnhancedPropBotDetailHeaderProps {
  bot: PropBot;
  mergedInstrument?: string;
  description?: string;
  isLoading?: boolean;
  isTranslated?: boolean;
  isOriginal?: boolean;
  isFallback?: boolean;
  originalLanguage?: string;
}

export const EnhancedPropBotDetailHeader = ({ 
  bot, 
  mergedInstrument, 
  description,
  isLoading,
  isTranslated = false,
  isOriginal = false,
  isFallback = false,
  originalLanguage = 'en'
}: EnhancedPropBotDetailHeaderProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // Robust extraction of propDetails - handle both array and object formats
  const propDetails = Array.isArray(bot.propDetails) && bot.propDetails.length > 0 
    ? bot.propDetails[0] 
    : bot.propDetails;
    
  const { startTour } = useTour();
  const isMobile = useIsMobile();

  // Detect if this is a shadow bot by checking name pattern
  const isShadowBot = bot.name.includes('(Shadow)') || bot.type === 'shadow_bot';
  
  // Extract original bot name if this is a shadow bot
  const originalBotName = isShadowBot ? bot.name.replace('(Shadow)', '').trim() : bot.name;
  
  // For now, we'll use a placeholder original bot ID - in real implementation this would come from the bot data
  const originalBotId = isShadowBot ? bot.id.replace('shadow-', '') : bot.id;

  const getRiskColor = (passRate: number | undefined): string => {
    if (!passRate) return "bg-gray-500/20 text-gray-500 border-gray-500/30";
    
    if (passRate >= 80) return "bg-green-500/20 text-green-500 border-green-500/30";
    if (passRate >= 60) return "bg-yellow-500/20 text-yellow-500 border-yellow-500/30";
    return "bg-red-500/20 text-red-500 border-red-500/30";
  };

  return (
    <div className="relative">
      {/* Shadow Bot Banner */}
      {isShadowBot && (
        <div className="mb-4 p-3 bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <GitBranch className="h-4 w-4 text-blue-500" />
              <div>
                <p className="text-sm font-medium text-blue-500">Shadow Bot</p>
                <p className="text-xs text-muted-foreground">
                  This is a shadow copy of "{originalBotName}" with reversed signals
                </p>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate(`/prop-trading/${originalBotId}`)}
              className="text-blue-500 border-blue-500/30 hover:bg-blue-500/10"
            >
              <ExternalLink className="h-3 w-3 mr-1" />
              View Original
            </Button>
          </div>
        </div>
      )}
      
      {/* Background with gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-muted/10 rounded-lg" />
      
      <Card className="relative border-0 bg-transparent shadow-xl">
        <CardHeader className={`${isMobile ? "p-4" : "p-6"} pb-0`}>
          <div className="flex flex-col lg:flex-row justify-between items-start gap-4" data-tour="prop-bot-info">
            {/* Left side - Bot info */}
            <div className="flex-1 space-y-3">
              <div className="flex items-center gap-3">
                {/* Animated bot icon */}
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-transparent rounded-2xl blur-xl animate-pulse" />
                  <div className={`relative ${isMobile ? "w-12 h-12" : "w-16 h-16"} bg-gradient-to-br from-primary/20 to-primary/10 rounded-2xl flex items-center justify-center border border-primary/20`}>
                    <Bot className={`${isMobile ? "h-6 w-6" : "h-8 w-8"} text-primary`} />
                  </div>
                </div>
                
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <h1 className={`${isMobile ? "text-xl" : "text-3xl"} font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent`}>
                      {isShadowBot ? originalBotName : bot.name}
                    </h1>
                    {isShadowBot && (
                      <Badge variant="outline" className="bg-blue-500/20 text-blue-500 border-blue-500/30">
                        <GitBranch className="h-3 w-3 mr-1" />
                        Shadow
                      </Badge>
                    )}
                    {!isMobile && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              className="rounded-full p-2 hover:bg-muted transition-all duration-300 hover:scale-110"
                              aria-label={t('start_page_tour')}
                              onClick={() => startTour("prop-trading-bot-detail")}
                            >
                              <Info className="h-5 w-5 text-primary" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <div className="space-y-1">
                              <div>{t('start_page_tour')}</div>
                              {mergedInstrument && (
                                <div className="text-xs text-muted-foreground">
                                  Instrument: {mergedInstrument}
                                </div>
                              )}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                  
                  {/* Badges and metadata */}
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge 
                      variant="outline" 
                      className={`${isMobile ? "text-xs py-0" : ""} ${getRiskColor(propDetails?.pass_rate)} animate-fade-in`}
                    >
                      <Shield className={`${isMobile ? "mr-0.5 h-3 w-3" : "mr-1 h-3 w-3"}`} />
                      {propDetails?.pass_rate ? `${propDetails.pass_rate}% ${t('pass_rate')}` : t('unknown_risk')}
                    </Badge>
                    
                    {propDetails?.estimated_pass_time && (
                      <Badge variant="outline" className={`${isMobile ? "text-xs py-0" : ""} bg-blue-500/20 text-blue-500 border-blue-500/30`}>
                        <Award className={`${isMobile ? "mr-0.5 h-3 w-3" : "mr-1 h-3 w-3"}`} />
                        {propDetails.estimated_pass_time} {t('days')}
                      </Badge>
                    )}
                    
                    {propDetails?.potential_profit && (
                      <Badge variant="outline" className={`${isMobile ? "text-xs py-0" : ""} bg-green-500/20 text-green-500 border-green-500/30`}>
                        <TrendingUp className={`${isMobile ? "mr-0.5 h-3 w-3" : "mr-1 h-3 w-3"}`} />
                        {propDetails.potential_profit}% {t('profit')}
                      </Badge>
                    )}
                  </div>
                  
                  {/* Metadata for desktop */}
                  {!isMobile && (
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        <span>{t('created')} {formatDate(bot.created_date)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Info className="h-4 w-4" />
                        <span>{t('bot_id')}: {bot.bot_id}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className={`${isMobile ? "px-4 pb-4" : "px-6 pb-6"} pt-4`}>
          <div className="space-y-4">
            {/* Description */}
            <div className="relative space-y-2">
              <div className="flex items-center gap-2">
                {!isLoading && (
                  <>
                    {isOriginal && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                        Original
                      </span>
                    )}
                    {isTranslated && (
                      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                        Translated
                      </span>
                    )}
                    {isFallback && (
                      <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
                        Original ({originalLanguage.toUpperCase()})
                      </span>
                    )}
                  </>
                )}
              </div>
              <p className={`${isMobile ? "text-sm" : "text-lg"} leading-relaxed text-muted-foreground`}>
                {description || bot.description}
              </p>
            </div>
            
            {/* Key info cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="group relative overflow-hidden rounded-xl border border-green-300/20 bg-gradient-to-br from-green-500/10 via-green-500/5 to-transparent p-4 transition-all duration-300 hover:shadow-lg hover:shadow-green-500/10 hover:-translate-y-1">
                <div className="absolute inset-0 bg-gradient-to-br from-green-500/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="relative">
                  <span className={`${isMobile ? "text-xs" : "text-sm"} font-medium text-muted-foreground uppercase tracking-wider`}>
                    {t('prop_firm')}
                  </span>
                  <p className={`${isMobile ? "text-base" : "text-xl"} font-bold mt-1`}>
                    {propDetails?.prop_firm || 'N/A'}
                  </p>
                </div>
              </div>
              
              <div className="group relative overflow-hidden rounded-xl border border-blue-300/20 bg-gradient-to-br from-blue-500/10 via-blue-500/5 to-transparent p-4 transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/10 hover:-translate-y-1">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="relative">
                  <span className={`${isMobile ? "text-xs" : "text-sm"} font-medium text-muted-foreground uppercase tracking-wider`}>
                    {t('required_capital')}
                  </span>
                  <p className={`${isMobile ? "text-base" : "text-xl"} font-bold mt-1`}>
                    ${(propDetails?.min_capital || 0).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
